import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Handlebars from 'handlebars';
import { API_URL } from '../../config';
import AdminLayout from '../../components/AdminLayout';

function AdminNewsletterPreview() {
  const [articlesData, setArticlesData] = useState([]);
  const [templateHtml, setTemplateHtml] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState('');
  const [sendError, setSendError] = useState('');

  const [allSubscribers, setAllSubscribers] = useState([]);
  const [selectedSubscribers, setSelectedSubscribers] = useState([]); // Array of email strings
  const [loadingSubscribers, setLoadingSubscribers] = useState(true);

  const token = localStorage.getItem('adminToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPreviewDataAndSubscribers = useCallback(async () => {
    setLoading(true);
    setLoadingSubscribers(true);
    setError(null);
    try {
      const [articlesRes, templateRes, subscribersRes] = await Promise.all([
        axios.get(`${API_URL}/articles/weekly-newsletter`, { headers }),
        axios.get(`${API_URL}/templates/weekly-newsletter`, { headers }),
        axios.get(`${API_URL}/subscriptions/active`, { headers }) // Fetch active subscribers
      ]);
      setArticlesData(articlesRes.data.data || []);
      setTemplateHtml(templateRes.data || '');
      setAllSubscribers(subscribersRes.data.data || []);
      
      console.log('Template Response Data:', templateRes.data);
      console.log('Template Response Status:', templateRes.status);
      console.log('Template Response Headers:', templateRes.headers);

    } catch (err) {
      console.error('Error fetching initial data:', err);
      const errorMsg = err.response?.data?.message || err.response?.data?.error || 'Failed to load initial data.';
      setError(errorMsg);
      // If one of the critical fetches failed, ensure related state is cleared
      if (err.config.url.includes('articles')) setArticlesData([]);
      if (err.config.url.includes('templates')) setTemplateHtml('');
      if (err.config.url.includes('subscriptions')) setAllSubscribers([]);
    }
    setLoading(false);
    setLoadingSubscribers(false);
  }, [token]); // headers is not stable, token is

  useEffect(() => {
    fetchPreviewDataAndSubscribers();
  }, [fetchPreviewDataAndSubscribers]);

  useEffect(() => {
    if (templateHtml && (articlesData.length > 0 || !loading)) { // Allow preview even with no articles
      try {
        const compiledTemplate = Handlebars.compile(templateHtml);
        const dataForTemplate = {
          categories: articlesData.length > 0 ? articlesData : [], // Ensure categories is an array
          currentYear: new Date().getFullYear(),
          newsletterDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        };
        setPreviewHtml(compiledTemplate(dataForTemplate));
        // Record preview view against the latest newsletter if available
        (async () => {
          try {
            const token = localStorage.getItem('adminToken');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const latestNewsletterRes = await axios.get(`${API_URL}/newsletters/latest`, { headers });
            const latest = latestNewsletterRes?.data?.data;
            if (latest && latest._id) {
              await fetch(`${API_URL}/analytics/newsletters/${latest._id}/preview-view`, { method: 'POST' });
            }
          } catch (_) {}
        })();
      } catch (compileError) {
        console.error('Error compiling Handlebars template:', compileError);
        setError('Failed to compile newsletter template for preview.');
        setPreviewHtml('<p>Error compiling template.</p>');
      }
    } else if (templateHtml && articlesData.length === 0 && !loading) {
        // If template is there, no articles, and not loading, show no articles message in preview
        try {
            const compiledTemplate = Handlebars.compile(templateHtml);
            const dataForTemplate = {
              categories: [],
              currentYear: new Date().getFullYear(),
              newsletterDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
            };
            setPreviewHtml(compiledTemplate(dataForTemplate)); 
        } catch (compileError) {
            console.error('Error compiling empty template:', compileError);
            setError('Failed to compile newsletter template for preview (no articles).');
            setPreviewHtml('<p>Error compiling template for no articles state.</p>');
        }
    }
  }, [templateHtml, articlesData, loading]);

  const handleArticleChange = (categoryIndex, articleIndex, field, value) => {
    const updatedData = [...articlesData];
    updatedData[categoryIndex].articles[articleIndex][field] = value;
    setArticlesData(updatedData);
  };

  const handleSubscriberSelectionChange = (event) => {
    const selectedOptions = Array.from(event.target.selectedOptions, option => option.value);
    setSelectedSubscribers(selectedOptions);
  };
  
  const handleSendNewsletter = async () => {
    setIsSending(true);
    setSendSuccess('');
    setSendError('');
    try {
      const payload = {
        articlesData, // Send the potentially edited articlesData
      };
      if (selectedSubscribers.length > 0) {
        payload.targetEmails = selectedSubscribers;
      }

      const response = await axios.post(`${API_URL}/newsletters/send-weekly-digest`, 
        payload, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSendSuccess(response.data.message || 'Newsletter sent successfully!');
    } catch (err) {
      console.error('Error sending newsletter:', err);
      setSendError(err.response?.data?.error || err.response?.data?.message || 'Failed to send newsletter.');
    }
    setIsSending(false);
  };

  if (loading) {
    return <AdminLayout><div className="text-center p-8">Loading preview and subscriber data...</div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">Weekly Newsletter Preview & Edit</h1>
        
        {error && <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert"><p>{error}</p></div>}

        {/* Subscriber Selection Section Added Below Edit Content */} 
        <div className="mb-8 p-6 bg-white shadow-lg rounded-lg">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Select Recipients</h2>
            {loadingSubscribers ? (
                <p>Loading subscribers...</p>
            ) : allSubscribers.length === 0 ? (
                <p>No active subscribers found.</p>
            ) : (
                <>
                    <p className="text-sm text-gray-600 mb-2">
                        Select specific subscribers to send this newsletter to. 
                        If no subscribers are selected, the newsletter will be sent to ALL active subscribers.
                    </p>
                    <select 
                        multiple 
                        value={selectedSubscribers}
                        onChange={handleSubscriberSelectionChange}
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        size={Math.min(10, allSubscribers.length + 1)} // Show up to 10, or fewer if less subscribers
                    >
                        {allSubscribers.map(subscriber => (
                            <option key={subscriber._id || subscriber.email} value={subscriber.email}>
                                {subscriber.name ? `${subscriber.name} (${subscriber.email})` : subscriber.email}
                            </option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Selected: {selectedSubscribers.length} subscriber(s). Use Ctrl/Cmd + Click to select multiple.</p>
                </>
            )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* Edit Section */}
          <div className="bg-white shadow-lg rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Edit Content</h2>
            {articlesData.length === 0 && !loading && <p>No articles to edit for this week's newsletter.</p>}
            {articlesData.map((category, catIndex) => (
              <div key={category.categoryId || catIndex} className="mb-6 pb-4 border-b last:border-b-0">
                <h3 className="text-lg font-medium text-indigo-700 mb-2">{category.categoryName}</h3>
                {category.articles.map((article, artIndex) => (
                  <div key={article._id} className="mb-3 p-3 bg-gray-50 rounded">
                    <label htmlFor={`title-${article._id}`} className="block text-sm font-medium text-gray-700">Title:</label>
                    <input 
                      type="text" 
                      id={`title-${article._id}`}
                      value={article.title}
                      onChange={(e) => handleArticleChange(catIndex, artIndex, 'title', e.target.value)}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    <label htmlFor={`summary-${article._id}`} className="mt-2 block text-sm font-medium text-gray-700">Summary (for email):</label>
                    <textarea 
                      id={`summary-${article._id}`}
                      value={article.summaryForEmail || article.description} // Use summaryForEmail if available
                      onChange={(e) => handleArticleChange(catIndex, artIndex, 'summaryForEmail', e.target.value)}
                      rows={3}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>

          {/* Preview Section */}
          <div className="bg-white shadow-lg rounded-lg p-1">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 p-5 border-b">Live Preview</h2>
            <div 
              className="newsletter-preview-iframe-container overflow-auto" 
              style={{ maxHeight: '80vh'}} // Adjust height as needed
            >
              {previewHtml ? (
                <iframe 
                  srcDoc={previewHtml}
                  title="Newsletter Preview"
                  style={{width: '100%', height: '75vh', border: 'none'}}
                  sandbox="allow-same-origin" // For security, if content is purely from your backend
                />
              ) : (
                <p className="p-5">Generating preview...</p>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 text-center">
          {sendError && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">{sendError}</div>}
          {sendSuccess && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4" role="alert">{sendSuccess}</div>}
          <button 
            onClick={handleSendNewsletter}
            disabled={isSending || error || articlesData.length === 0}
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg text-lg focus:outline-none focus:shadow-outline disabled:opacity-50"
          >
            {isSending ? 'Sending Newsletter...' : 
              (selectedSubscribers.length > 0 ? `Send to ${selectedSubscribers.length} Selected Subscribers` : 'Send to All Subscribers')
            }
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminNewsletterPreview; 